import joi from 'joi';
import React, { useCallback, useMemo, useState } from 'react';
import { Box, FormControlLabel, Switch, SxProps } from '@mui/material';
import { colorPalette, sxs } from '@qualifio/ui-commons';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useTranslation } from 'react-i18next';
import { EditorSidebarHeader } from '../EditorSidebarHeader';
import { EmbeddedMediaWidgetSidebarAlert } from './EmbeddedMediaWidgetSidebarAlert';
import { ReferralSidebarAlert } from './ReferralSidebarAlert';
import { RssSidebarAlert } from './RssSidebarAlert';
import { STYLES_ID, TRAITS_ID } from '../../../config';
import { TraitForm } from '../../../editor/components/utils/TraitForm';
import { editorComponentsDefinitions } from '../../../editor/components/components';
import { sidebarStyle } from '../sidebars-style';
import { useEditorStore } from '../../../store';
import { useReferralConfig } from '../../../../referral/ReferralConfigContext';

function titleCase(id: string): string {
  return id
    .replace(
      /((^|-|_)+)([a-z])/gi,
      (match, separator, separators, letter) => ` ${letter.toLocaleUpperCase()}`,
    ) // Capitalize after separators and first letter
    .replace(/([a-z])([A-Z])/g, (match, end, start) => `${end} ${start.toUpperCase()}`) // Add spaces in camelCase
    .trim();
}

export interface EditorComponentSidebarProps {
  sx?: SxProps;
}

export function EditorComponentSidebar({
  sx = {},
}: EditorComponentSidebarProps): React.ReactElement {
  const { t } = useTranslation();
  const { referralConfig } = useReferralConfig();

  // Use Zustand store
  const selectedComponent = useEditorStore(state => state.selectedComponent);
  const unSelectComponent = useEditorStore(state => state.unSelectComponent);
  const locale = useEditorStore(state => state.locale);
  const markAsDirty = useEditorStore(state => state.markAsDirty);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleClose = useCallback(() => unSelectComponent(), [unSelectComponent]);

  const componentDefinition = useMemo(
    () =>
      selectedComponent
        ? editorComponentsDefinitions.find(
            definition => definition.id === selectedComponent.attributes.type,
          )
        : undefined,
    [selectedComponent],
  );

  const toggleShowAdvanced = useCallback(() => setShowAdvanced(showAdvanced => !showAdvanced), []);

  const hasTraits = selectedComponent?.attributes.traits.length > 0;
  const isReferralWidget = selectedComponent?.attributes.traits.models.some(
    (model: { id: string }) => model.id === 'referralType',
  );
  const isRssWidget = selectedComponent?.attributes.traits.models.some(
    (model: { id: string }) => model.id === 'feedUrl',
  );
  const isMediaWidget = selectedComponent?.attributes.traits.models.some(
    (model: { id: string }) => model.id === 'embedded-media',
  );

  const fallbackTitle = titleCase(selectedComponent?.attributes.type || '');

  const i18nextParserWorkaround = t;

  return (
    <Box sx={sxs(sidebarStyle, sx)}>
      <EditorSidebarHeader
        icon={
          typeof componentDefinition?.icon === 'string' ? (
            <div
              style={{ height: '1em' }}
              dangerouslySetInnerHTML={{ __html: componentDefinition?.icon }}
            ></div>
          ) : (
            componentDefinition?.icon
          )
        }
        title={
          componentDefinition?.getLabel({
            t: key => i18nextParserWorkaround(key, fallbackTitle),
          }) || fallbackTitle
        }
        onClose={handleClose}
      />

      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          width: '100%',
          overflow: 'auto',
        }}
      >
        <div id={TRAITS_ID} />
        {selectedComponent && componentDefinition?.traits && (
          <TraitForm
            key={selectedComponent.cid}
            schema={componentDefinition?.traits.schema || joi.any()}
            value={{
              attributes: selectedComponent.attributes.attributes,
              i18nAttributes: selectedComponent.attributes.i18nAttributes?.[locale],
            }}
            onChange={val => {
              // force next tick
              setTimeout(() => {
                selectedComponent.set('attributes', val.attributes);
                selectedComponent.set('i18nAttributes', {
                  ...selectedComponent.attributes.i18nAttributes,
                  [locale]: val.i18nAttributes,
                });
                selectedComponent.view.onRender();
                markAsDirty();
              });
            }}
          >
            <componentDefinition.traits.form />
          </TraitForm>
        )}
        {!hasTraits && !componentDefinition?.traits ? (
          <Box
            sx={{
              color: colorPalette.grey.dark,
              borderBottom: `1px solid ${colorPalette.grey.main}`,
            }}
            p={2}
          >
            {t('editor.sidebar.traits.noTraitsForThisComponent')}
          </Box>
        ) : isReferralWidget && !getLocalizedProperty(referralConfig, 'link')?.length ? (
          <ReferralSidebarAlert />
        ) : isRssWidget ? (
          <RssSidebarAlert />
        ) : isMediaWidget ? (
          <EmbeddedMediaWidgetSidebarAlert />
        ) : (
          <></>
        )}

        <FormControlLabel
          control={<Switch onChange={toggleShowAdvanced} value={showAdvanced} />}
          label={t('editor.sidebar.advanced-design')}
          sx={{
            paddingY: 1,
            paddingX: 0.5,
            width: '100%',
            marginRight: 0,
            marginLeft: 0,
          }}
        />
        <Box id={STYLES_ID} sx={{ visibility: showAdvanced ? 'visible' : 'hidden' }} />
      </Box>
    </Box>
  );
}
